"use client";

import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";

interface CommunityBoardShellProps {
  /** 데스크탑 custom 셸 좌측 사이드바(customLeftSlot)에 portal 할 콘텐츠 — 필터 네비 + 새 이슈 CTA */
  left: ReactNode;
  /** 우측 사이드바(customRightSlot)에 portal 할 콘텐츠 — 안내·이슈 정보·진행 이력 카드 */
  right?: ReactNode;
  /** 셸 밖(모바일/태블릿, <1024px)에서 본문 **위**에 그릴 대체 콘텐츠(예: 가로 필터 칩) */
  leftFallback?: ReactNode;
  /** 셸 밖에서 본문 **아래**에 그릴 대체 콘텐츠(예: 진행 이력·관리자 패널) */
  rightFallback?: ReactNode;
  /** 두 모드 모두에서 맨 마지막에 그릴 콘텐츠(예: 댓글 스레드) */
  bottom?: ReactNode;
  children: ReactNode;
}

/**
 * 커뮤니티 게시판(질문·버그 `/community/issues`, 감자단 잡담 `/community/talk`, JCG-3029) 3컬럼 custom 셸 슬롯 배선.
 *
 * 두 게시판은 `isCommunityIssuesPage`/`isCommunityTalkPage` 로 `ArticleShell variant="custom"` 을
 * 타고(포럼 인덱스·/my 대시보드와 같은 경로), 좌/우 사이드바는 페이지가 이 컴포넌트로 슬롯에
 * `createPortal` 한다(JCG-2063 [D] 패턴). 서버 컴포넌트 페이지가 만든 ReactNode(네비·카드)를 그대로
 * 받아 꽂으므로 사이드바 내용은 서버에서 데이터(카운트·이슈 정보)와 함께 렌더된다.
 *
 * 셸이 없는 모바일/태블릿(`inShell=false`)에서는 단일 컬럼 폴백: leftFallback → children →
 * rightFallback → bottom 순서. 슬롯 div 는 셸 마운트 뒤 생기므로 slot 이 null 이면 portal 을 미룬다.
 */
export default function CommunityBoardShell({
  left,
  right,
  leftFallback,
  rightFallback,
  bottom,
  children,
}: CommunityBoardShellProps) {
  const { inShell, customLeftSlot, customRightSlot } = useArticleShellSlots();

  if (!inShell) {
    return (
      <>
        {leftFallback}
        {children}
        {rightFallback}
        {bottom}
      </>
    );
  }

  return (
    <>
      {children}
      {bottom}
      {customLeftSlot && createPortal(left, customLeftSlot)}
      {customRightSlot && right && createPortal(right, customRightSlot)}
    </>
  );
}
